import React, { useState, useEffect } from 'react';
import {
  Tabs,
  Button,
  Table,
  Tag,
  Input,
  Row,
  Col,
  Space,
  Form,
  Card,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  SendOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import ChargingHttpHandler from '../../../shared/chargingHttpHandler';
import secureStorage from '../../../shared/secureStorage';
import ExtraSearch from './ExtraSearch';

// 補回 roleData 假資料
const roleData = [
  {
    key: '1',
    role: '管理員',
    customer: true,
    charge: true,
    money: true,
    push: true,
    stat: true,
    log: true,
  },
  {
    key: '2',
    role: '操作員',
    customer: true,
    charge: true,
    money: true,
    push: true,
    stat: true,
    log: false,
  },
  {
    key: '3',
    role: '查看者',
    customer: 'view',
    charge: 'view',
    money: 'view',
    push: 'view',
    stat: 'view',
    log: false,
  },
];

const UserTab = () => {
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);

  const getRoleName = (roleId) => {
    // 測試區
    // if (roleId === 28 || roleId === '28') return '管理員';
    // if (roleId === 29 || roleId === '29') return '操作員';
    // 正式區
    if (roleId === 4 || roleId === '4') return '充電APP權限';
    if (roleId === 5 || roleId === '5') return '充電樁後台操作員';
    if (roleId === 6 || roleId === '6') return '充電樁後台管理員';
    return '未知';
  };

  useEffect(() => {
    async function fetchUsers() {
      setLoading(true);
      const httpHandler = new ChargingHttpHandler(false); // 不自動帶 token
      const chargingAccessToken = secureStorage.getItem('chargingAccessToken');
      const loginTime = secureStorage.getItem('userLoginTime');
      console.log('loginTime', loginTime);
      try {
        // 1. 取得所有有權限的 user
        const usersRes = await httpHandler
          .axios()
          .get('/platform/users_by_roles?role_ids=4,5,6', {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${chargingAccessToken}`,
            },
          });
        let users = [];
        if (usersRes && usersRes.data && Array.isArray(usersRes.data)) {
          users = usersRes.data;
        }

        // 2. 取得所有用戶的 last_login_time
        const loginTimeRes = await httpHandler
          .axios()
          .get('/platform/get_user_last_login_time', {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${chargingAccessToken}`,
            },
          });
        let loginTimeMap = {};
        if (
          loginTimeRes &&
          loginTimeRes.data &&
          Array.isArray(loginTimeRes.data.users)
        ) {
          loginTimeRes.data.users.forEach((u) => {
            loginTimeMap[u.account] = u.last_login_time;
          });
        }

        // 3. 合併資料
        setUserData(
          users.map((user) => ({
            key: user.user_id,
            id: user.user_id,
            username: user.user_name,
            role: getRoleName(user.role_id),
            email: user.user_account,
            lastLogin: loginTimeMap[user.user_account] || '',
            status: '啟用',
          }))
        );
      } catch (e) {
        console.error('Failed to fetch user data:', e);
        setUserData([]);
      } finally {
        setLoading(false);
      }
    }
    fetchUsers();
  }, []);

  return (
    <div style={{ background: '#fff', padding: 24, borderRadius: 12 }}>
      <Table
        columns={[
          { title: '用戶ID', dataIndex: 'id' },
          { title: '用戶名', dataIndex: 'username' },
          { title: '角色', dataIndex: 'role' },
          { title: '電子郵件', dataIndex: 'email' },
          { title: '最後登入時間', dataIndex: 'lastLogin' },
          {
            title: '狀態',
            dataIndex: 'status',
            render: (text) =>
              text === '啟用' ? (
                <Tag color="blue">啟用</Tag>
              ) : (
                <Tag color="gold">停用</Tag>
              ),
          },
        ]}
        dataSource={userData}
        loading={loading}
        pagination={false}
        bordered={false}
        style={{ background: '#fff', borderRadius: 12 }}
      />
    </div>
  );
};

const RoleTab = () => (
  <div style={{ background: '#fff', padding: 24, borderRadius: 12 }}>
    <Table
      columns={[
        { title: '角色', dataIndex: 'role' },
        {
          title: '客戶管理',
          dataIndex: 'customer',
          render: (v) =>
            v === true ? (
              <span>✔️</span>
            ) : v === 'view' ? (
              <span role="img" aria-label="view">
                👁️
              </span>
            ) : (
              <span style={{ color: '#f5222d' }}>✖️</span>
            ),
        },
        {
          title: '充電歷程',
          dataIndex: 'charge',
          render: (v) =>
            v === true ? (
              <span>✔️</span>
            ) : v === 'view' ? (
              <span role="img" aria-label="view">
                👁️
              </span>
            ) : (
              <span style={{ color: '#f5222d' }}>✖️</span>
            ),
        },
        {
          title: '充電金管理',
          dataIndex: 'money',
          render: (v) =>
            v === true ? (
              <span>✔️</span>
            ) : v === 'view' ? (
              <span role="img" aria-label="view">
                👁️
              </span>
            ) : (
              <span style={{ color: '#f5222d' }}>✖️</span>
            ),
        },
        {
          title: '推播平台',
          dataIndex: 'push',
          render: (v) =>
            v === true ? (
              <span>✔️</span>
            ) : v === 'view' ? (
              <span role="img" aria-label="view">
                👁️
              </span>
            ) : (
              <span style={{ color: '#f5222d' }}>✖️</span>
            ),
        },
        {
          title: '統計分析',
          dataIndex: 'stat',
          render: (v) =>
            v === true ? (
              <span>✔️</span>
            ) : v === 'view' ? (
              <span role="img" aria-label="view">
                👁️
              </span>
            ) : (
              <span style={{ color: '#f5222d' }}>✖️</span>
            ),
        },
        {
          title: '後台歷程',
          dataIndex: 'log',
          render: (v) =>
            v === true ? (
              <span>✔️</span>
            ) : v === 'view' ? (
              <span role="img" aria-label="view">
                👁️
              </span>
            ) : (
              <span style={{ color: '#f5222d' }}>✖️</span>
            ),
        },
      ]}
      dataSource={roleData}
      pagination={false}
      bordered={false}
      style={{ background: '#fff', borderRadius: 12 }}
    />
  </div>
);

const LogTab = () => {
  const [form] = Form.useForm();
  const [originLogData, setOriginLogData] = useState([]);
  const [filterVisible, setFilterVisible] = useState(false);
  const [userInfo, setUserInfo] = useState([]);
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState('');
  const [logData, setLogData] = useState([]);
  const [loading, setLoading] = useState(false);

  const httpHandler = new ChargingHttpHandler(false);
  const chargingAccessToken = secureStorage.getItem('chargingAccessToken');

  const fetchLogData = async () => {
    setLoading(true);

    try {
      const response = await httpHandler
        .axios()
        .get('/platform/get_user_log_table', {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${chargingAccessToken}`,
          },
        });

      if (response.data && response.data.user_logtab) {
        const formattedData = response.data.user_logtab.map((log, index) => ({
          key: index,
          time: moment(log.log_time).format('YYYY-MM-DD HH:mm:ss'),
          user: log.user_role,
          user_ip: log.user_ip,
          type: log.action_type,
          content: log.action_content,
          status: log.status === 1 ? '成功' : '失敗',
        }));
        setOriginLogData(formattedData);
        setLogData(formattedData);
      }
    } catch (error) {
      console.error('Failed to fetch log data:', error);
      setLogData([]);
    } finally {
      setLoading(false);
    }
  };

  // 獲取資料
  useEffect(() => {
    fetchLogData();
  }, []);

  // 清除篩選
  const clearFilters = () => {
    fetchLogData();
    setFilters({});
    form.resetFields();
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Card>
          <Row gutter={[16, 16]} align="middle">
            <Col flex="auto">
              <Input
                placeholder="搜尋操作記錄"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                prefix={<SearchOutlined />}
                style={{ width: 200 }}
              />
            </Col>
            <Col>
              <Space>
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => {
                    setLoading(true);
                    httpHandler
                      .axios()
                      .get('/platform/get_user_info', {
                        headers: {
                          Authorization: `Bearer ${chargingAccessToken}`,
                        },
                      })
                      .then((response) => {
                        if (response.data) {
                          setUserInfo(
                            response.data.data.map((item) => ({
                              value: item.user_id,
                              label: item.name,
                            }))
                          );
                        }
                      })
                      .finally(() => {
                        setLoading(false);
                        setFilterVisible(true);
                      });
                  }}>
                  篩選
                </Button>
                <Button onClick={clearFilters}>清除</Button>
                <Button
                  icon={<SendOutlined />}
                  onClick={clearFilters}
                  loading={loading}>
                  重新載入
                </Button>
              </Space>
            </Col>
          </Row>
          {/* 篩選條件顯示 */}
          {Object.keys(filters).length > 0 && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
              篩選條件:
              {Object.entries(filters)
                .filter(([key, value]) => {
                  // 只顯示有實際值的篩選條件
                  if (Array.isArray(value)) {
                    return value.length > 0;
                  } else {
                    return value && value.toString().trim() !== '';
                  }
                })
                .map(([key, value]) => {
                  // 根據欄位名稱顯示對應的中文標籤~
                  const fieldLabels = {
                    operationDate: '操作時間',
                    operationType: '操作類型',
                    operator: '操作人員',
                    operationContent: '操作內容',
                  };

                  const fieldLabel = fieldLabels[key] || key;

                  return <Tag key={key}>{fieldLabel}</Tag>;
                })}
            </div>
          )}
        </Card>
      </div>
      <div style={{ background: '#fff', padding: 24, borderRadius: 12 }}>
        {/* 進階篩選 Modal */}
        <ExtraSearch
          setFilters={setFilters}
          form={form}
          setFilterVisible={setFilterVisible}
          filterVisible={filterVisible}
          filters={filters}
          setLogData={setLogData}
          fetchLogData={fetchLogData}
          userInfo={userInfo}
          originLogData={originLogData}
        />
        <Table
          columns={[
            { title: '時間', dataIndex: 'time' },
            { title: '用戶', dataIndex: 'user' },
            {
              title: '用戶IP',
              dataIndex: 'user_ip',
              render: (text) => {
                const check = secureStorage.getItem('check');
                // 測試區為 28，正式區為 6
                if (check === 6) {
                  return text;
                }
                return text.replace(/^(\d+\.\d+)\.\d+\.\d+$/, '$1.***.***');
              },
            },
            { title: '操作類型', dataIndex: 'type' },
            { title: '操作內容', dataIndex: 'content' },
            {
              title: '狀態',
              dataIndex: 'status',
              render: (text) => (
                <Tag color={text === '成功' ? 'success' : 'error'}>{text}</Tag>
              ),
            },
          ]}
          dataSource={logData.filter((log) =>
            search
              ? log.user.toLowerCase().includes(search.toLowerCase()) ||
                log.type.toLowerCase().includes(search.toLowerCase()) ||
                log.content.toLowerCase().includes(search.toLowerCase())
              : true
          )}
          loading={loading}
          pagination={{ pageSize: 10 }}
          bordered={false}
        />
      </div>
    </div>
  );
};

const LogHistory = () => {
  return (
    <div style={{ padding: 32, background: '#f7faf9', minHeight: '100vh' }}>
      <div style={{ fontSize: 32, fontWeight: 700, marginBottom: 24 }}>
        後台歷程
      </div>
      <Tabs
        defaultActiveKey="log"
        items={[
          { key: 'log', label: '操作日誌', children: <LogTab /> },
          { key: 'user', label: '用戶管理', children: <UserTab /> },
          { key: 'role', label: '權限設定', children: <RoleTab /> },
        ]}
      />
    </div>
  );
};

export default LogHistory;
